import { Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { webCodecsAvailable } from '../../lib/compress-video';
import {
  type VideoKind,
  useListVideosQuery,
} from '../../store/coachApi';
import Modal from '../Modal';
import { useVideoCaptureAvailability } from './exercise-video-form';

const KIND_KEY: Record<VideoKind, string> = {
  library: 'Текущий в библиотеке',
  active: 'Активный план',
  archive: 'Архив',
  unattached: 'Не привязан',
};

interface ExerciseVideoSheetProps {
  name: string;
  hasVideo: boolean;
  onClose: () => void;
  onRemove: () => void;
  onDelete: () => void;
  onPickFile: (file: File) => void;
  onPickExisting: (videoId: string) => void;
}

const ExerciseVideoSheet = ({
  name,
  hasVideo,
  onClose,
  onRemove,
  onDelete,
  onPickFile,
  onPickExisting,
}: ExerciseVideoSheetProps) => {
  const { t } = useTranslation();
  const { captureEnabled, showUpdateBrowser } = useVideoCaptureAvailability();
  const fileRef = useRef<HTMLInputElement>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const { data: videos, isLoading, isFetching, error, refetch } = useListVideosQuery(undefined, {
    skip: !libraryOpen,
  });

  const takeFile = (list: FileList | null) => {
    const file = list?.[0];
    if (!file) return;
    onPickFile(file);
    onClose();
  };

  const openDevicePicker = () => {
    const input = fileRef.current;
    if (!webCodecsAvailable() || navigator.onLine === false || !input) return;
    input.value = '';
    input.click();
  };

  return (
    <Modal onClose={onClose} panelClassName="sm:max-w-md">
      <div className="modal-header flex justify-between items-start gap-4">
        <h2 className="text-xl font-bold text-slate-900 tracking-tight pr-2">
          {name.trim() || t('Упражнения')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost shrink-0 -mr-1 -mt-1"
          aria-label={t('Закрыть')}
        >
          <X size={22} />
        </button>
      </div>

      {libraryOpen ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setLibraryOpen(false)}
            className="btn-secondary w-full min-h-12"
          >
            {t('Назад')}
          </button>
          {isLoading || (isFetching && !videos) ? (
            <div className="flex justify-center py-8">
              <Loader2 size={22} className="animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="space-y-3">
              <p className="min-w-0 break-words rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-medium leading-relaxed text-red-600">
                {error.message || t('Не удалось загрузить ролики')}
              </p>
              <button type="button" onClick={() => void refetch()} className="btn-secondary w-full min-h-12">
                {t('Повторить')}
              </button>
            </div>
          ) : !videos?.length ? (
            <p className="py-6 text-center text-sm font-medium text-slate-500">{t('Нет роликов')}</p>
          ) : (
            <div className="max-h-[50vh] space-y-2 overflow-y-auto">
              {videos.map((video) => {
                const title = video.label || t(KIND_KEY[video.kind]);
                return (
                  <button
                    key={video.id}
                    type="button"
                    onClick={() => {
                      onPickExisting(video.id);
                      onClose();
                    }}
                    className="flex w-full min-h-12 flex-col items-start justify-center rounded-2xl border border-app-border bg-white px-4 py-3 text-left"
                  >
                    <span className="w-full break-words text-sm font-medium text-slate-900">{title}</span>
                    {video.label && (
                      <span className="mt-0.5 text-xs text-slate-500">{t(KIND_KEY[video.kind])}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <button type="button" onClick={onClose} className="btn-secondary w-full min-h-12">
            {t('Оставить как есть')}
          </button>
          <button
            type="button"
            className="btn-secondary w-full min-h-12 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!captureEnabled}
            onClick={openDevicePicker}
          >
            {t('Загрузить')}
          </button>
          <button
            type="button"
            className="btn-secondary w-full min-h-12"
            onClick={() => setLibraryOpen(true)}
          >
            {t('Выбрать из галереи')}
          </button>
          {showUpdateBrowser && (
            <p className="text-sm text-center text-slate-500">{t('Обновите браузер')}</p>
          )}
          {hasVideo && (
            <button
              type="button"
              onClick={() => {
                onRemove();
                onClose();
              }}
              className="btn-secondary w-full min-h-12 text-red-600"
            >
              {t('Убрать ролик')}
            </button>
          )}
          {hasVideo && (
            <button
              type="button"
              onClick={onDelete}
              className="btn-secondary w-full min-h-12 text-red-600"
            >
              {t('Удалить ролик')}
            </button>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => takeFile(e.target.files)}
      />
    </Modal>
  );
};

export default ExerciseVideoSheet;
